import React, { useState, useEffect, useRef } from 'react';
import { Heart, MessageCircle, Eye, TrendingUp, Flame, Leaf, Beaker, Award,Lock, Users, ArrowRight, Target, Download, BookOpen, Clock, ChevronUp, ChevronDown, Share2, Bookmark, Filter, Search, Sparkles, Zap, Star, Trophy, Gift, Bell, Plus, X, Video, Microscope, UserCircle } from 'lucide-react';
import { PollComponent } from '../components/Poll';
import { CreatePostModal } from '../components/CreatePostModal';
import { UserLevelCard } from '../components/UserLevelCard';
import { TrendingCard } from '../components/TrendingCard';
import { CommunityStatsCard } from '../components/CommunityStats';
import { PostCard } from '../components/PostComponent'; 
import axios from 'axios';
import API_URL from '../config/ApiUrl';
import { useUser } from '../context/UserContext';
import { AchievementsCard } from '../components/AchievementsCard';
import ResearchArticleDetail from './ResearchArticlePage';
import { researchAPI } from '../services/ResearchServices';
import PublicProfileView from '../profile_component/PublicProfileViewPage';
import { useTheme } from '../arrangement/ThemeContext';
import ThreadDetailView from './ThreadDetailsPage';
// Add MobileStatsToggle component from threads page
const MobileStatsToggle = ({ isMobile, colors }) => {
  const [activeTab, setActiveTab] = useState('trending');

  return (
    <div style={{
      background: colors.bgTertiary1,
      borderRadius: '16px',
      border: '1px solid #333',
      overflow: 'hidden'
    }}>
      {/* Toggle Header */}
      <div style={{
        display: 'flex',
        background: colors.bgCard,
        borderBottom: '1px solid #333'
      }}>
        <button
          onClick={() => setActiveTab('trending')}
          style={{
            flex: 1,
            padding: '12px 16px',
            background: activeTab === 'trending' 
              ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' 
              : 'transparent',
            color: activeTab === 'trending' ? 'white' : '#888',
            border: 'none',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          📈 Trending
        </button>
        <button
          onClick={() => setActiveTab('stats')}
          style={{
            flex: 1,
            padding: '12px 16px',
            background: activeTab === 'stats' 
              ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' 
              : 'transparent',
            color: activeTab === 'stats' ? 'white' : '#888',
            border: 'none',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          👥 Community
        </button>
      </div>

      {/* Content Area */}
      <div style={{ 
        padding: '0',
        background: 'transparent'
      }}>
        {activeTab === 'trending' ? (
          <TrendingCard isMobile={isMobile} />
        ) : (
          <CommunityStatsCard />
        )}
      </div>
    </div>
  );
};

const parseRelativeTime = (relativeTimeStr) => {
  console.log('===============================')
  console.log('===============================', relativeTimeStr)
  if (!relativeTimeStr || typeof relativeTimeStr !== 'string') {
    return new Date();
  }

  const now = new Date();
  const timeStr = relativeTimeStr.toLowerCase().trim();

  // Handle "just now" or similar
  if (timeStr.includes('just now') || timeStr.includes('now')) {
    return now;
  }

  // Extract number and unit
  const match = timeStr.match(/(\d+)([smhd])/);
  if (!match) {
    console.warn('Could not parse relative time:', relativeTimeStr);
    return now;
  }

  const value = parseInt(match[1]);
  const unit = match[2];

  // Calculate the past date
  switch (unit) {
    case 's': return new Date(now.getTime() - (value * 1000));
    case 'm': return new Date(now.getTime() - (value * 60 * 1000));
    case 'h': return new Date(now.getTime() - (value * 60 * 60 * 1000));
    case 'd': return new Date(now.getTime() - (value * 24 * 60 * 60 * 1000));
    default:
      console.warn('Unknown time unit:', unit);
      return now;
  }
};

const getContentDate = (item, type) => {
  if (!item) return [new Date(), null];
  
  let dateString;
  
  switch (type) {
    case 'thread':
      dateString = item.created_at;
      break;
    case 'sesh_activity':
      dateString = item.created_at;
      break;
    case 'research':
      dateString = item.created_at || item.published_date || item.submitted_at;
      break;
    default:
      dateString = item.created_at;
  }
  
  if (!dateString) {
    console.warn(`No date field found for ${type}:`, item);
    return [new Date(), null];
  }

  if (typeof dateString === 'string' && dateString.includes('ago')) {
    const parsedDate = parseRelativeTime(dateString);
    return [parsedDate, dateString];
  } else {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date format for ${type}:`, dateString);
      return [new Date(), dateString];
    }
    return [date, dateString];
  }
};

const normalizeContentItem = (item, type) => {
  const [contentDate, dateString] = getContentDate(item, type);

  const sortDate = contentDate.getTime();

  switch (type) {
    case 'thread':
      return {
        id: `thread_${item.id}`,
        type: 'thread',
        title: item.title,
        content: item.description,
        author: item.author_handle || item.author,
        avatar: item.avatar || item.user_avatar,
        author_user_id: item.author_user_id,
        createdAt: dateString,
        sortDate: dateString,
        originalDate: contentDate,
        votes: item.likes || 0,
        userVote: item.user_vote === 1 ? 1 : item.user_vote === -1 ? -1 : 0,
        comments: item.messages || 0,
        views: item.views || 0,
        tags: extractHashtags(item.title + ' ' + (item.description || '')),
        trending: item.trending || false,
        hot: item.likes > 20,
        streak: 0,
        verified: item.author_smoke_level === 'connoisseur',
        awards: [],
        engagement: calculateEngagement(item.likes, item.messages, item.views),
        originalData: item,
        sourceType: item.sesh_room_info ? 'sesh_thread' : 'personal_thread',
        seshRoomInfo: item.sesh_room_info,
        pollData: item.type === 'poll' ? {
          pollOptions: item.pollOptions,
          totalPollVotes: item.totalPollVotes,
          pollEndsIn: item.pollEndsIn,
          userVoted: item.poll?.has_voted
        } : null
      };
    
    case 'sesh_activity':
      return {
        id: `sesh_${item.id}`,
        type: 'sesh_room',
        title: `🎥 Live in ${item.title || item.name}`,
        content: item.description || `Join the live session! ${item.participant_count || 0} members active`,
        author: item.host?.username || 'Anonymous',
        avatar: item.host?.profile_image_url || getDefaultAvatar('casual'),
        createdAt: formatTimeAgo(contentDate),
        sortDate: sortDate,
        originalDate: contentDate,
        votes: 0,
        userVote: 0,
        comments: item.message_count || 0,
        views: item.viewer_count || 0,
        tags: [item.category, ...(item.tags || [])].filter(Boolean),
        trending: item.is_live,
        hot: item.is_live && (item.participant_count || 0) > 5,
        streak: 0,
        verified: !item.is_public,
        awards: item.is_live ? ['🔴'] : [],
        engagement: calculateSeshEngagement(item.participant_count, item.viewer_count, item.message_count),
        originalData: item,
        sourceType: 'sesh_room',
        isLive: item.is_live,
        participantCount: item.participant_count || 0,
        category: item.category,
        strain: item.strain
      };
    
    case 'research':
      return {
        id: `research_${item.id}`,
        type: 'research',
        title: `📚 ${item.title}`,
        content: item.description || item.subtitle,
        author: item.author,
        author_user_id: item.submitter?.id,
        avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${item.author}`,
        createdAt: formatTimeAgo(contentDate),
        sortDate: sortDate,
        originalDate: contentDate,
        votes: item.like_count || 0,
        userVote: 0,
        comments: item.discussion_count || 0,
        views: item.view_count || 0,
        tags: item.tags || [],
        trending: item.is_trending || false,
        hot: item.is_hot || false,
        streak: 0,
        verified: item.is_peer_reviewed || false,
        awards: item.is_peer_reviewed ? ['🏆'] : [],
        engagement: item.engagement_score || calculateEngagement(item.like_count, item.discussion_count, item.view_count),
        originalData: item,
        sourceType: 'research',
        category: item.category,
        readTime: item.read_time,
        difficulty: item.difficulty,
        institution: item.institution
      };
    
    default:
      return {
        ...item,
        createdAt: formatTimeAgo(contentDate),
        sortDate: sortDate,
        originalDate: contentDate
      };
  }
};

const getDefaultAvatar = (smokeLevel) => {
  const avatarMap = {
    stoner: '/peng1.png',
    beginner: '/chick1.png',
    connoisseur: '/dog.png',
    casual: '/pengu3.png'
  };
  return avatarMap[smokeLevel] || avatarMap.casual;
};

const formatTimeAgo = (dateString) => {
  console.log('-----------------------------------------------')
  console.log(dateString)
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
  
  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;
  return date.toLocaleDateString();
};

const extractHashtags = (text) => {
  if (!text) return [];
  const hashtags = text.match(/#[\w]+/g);
  return hashtags ? hashtags.map(tag => tag.slice(1).toLowerCase()) : [];
};

const calculateEngagement = (likes = 0, comments = 0, views = 0) => {
  if (views === 0) return 0;
  return Math.min(Math.round(((likes * 3 + comments * 2) / Math.max(views, 1)) * 100), 100);
};

const calculateSeshEngagement = (participants = 0, viewers = 0, messages = 0) => {
  const totalUsers = participants + viewers;
  if (totalUsers === 0) return 0;
  return Math.min(Math.round(((participants * 5 + messages) / Math.max(totalUsers, 1)) * 10), 100);
};

// Enhanced Community Post Card with mobile responsiveness
const CommunityPostCard = ({ post, onVote, onBookmark, onShare, onReward, onOpenArticle, onOpenThreadDetail, setShowProfileView, setSelectedUserId, getCurrentUserId,isAuthenticated, isMobile, colors }) => {
  const [tags, setTags] = useState([]);

  const handleUsernameClick = (userId) => {
    if (userId) {
      setSelectedUserId(userId);
      setShowProfileView(true);
    }
  };

  const getCategoryTheme = (category) => {
    const themes = {
      medical: { 
        icon: <Microscope size={32} />, 
        gradient: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        bgPattern: '#1e40af15'
      },
      growing: { 
        icon: <Leaf size={32} />, 
        gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        bgPattern: '#05966915'
      },
      strains: { 
        icon: <Beaker size={32} />, 
        gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        bgPattern: '#7c3aed15'
      },
      general: { 
        icon: <BookOpen size={32} />, 
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        bgPattern: '#d9770615'
      }
    };
    return themes[category] || themes.general;
  };

  const getSmokerEmoji = (level) => {
    switch(level) {
      case 'beginner': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/cdb53b7a8c7e4624a810c0d654ca03c8_Smoker_beginer.webp';
      case 'casual': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/e72017f33d1c480db1928ca3d7295de4_Smoker_Level_cas.webp';
      case 'connoisseur': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/3e1e13711dd441fcb7c34c155416ee69_Smoker_Level_con.webp';
      case 'stoner': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/9084554e27d747188148530bc7899eee_Smoker_Level_Logo_Stoner.webp';
      default: return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/6d4ba06007624c3691fd682dfbb31cdf_hmind.png';
    }
  };

  // Thread Card Component with mobile responsiveness
  const ThreadCard = () => (
    <div style={{
      background: colors.bgPrimary,
      borderRadius: '20px',
      padding: isMobile ? '16px' : '24px',
      border: '1px solid #333',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = '#00d4aa';
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 212, 170, 0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = '#333';
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
    >
      {/* Main content with voting - mobile responsive layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: isMobile ? '12px' : '20px',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        
        {/* Voting Section - Mobile: Top (horizontal), Desktop: Left (vertical) */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'flex-start',
          gap: isMobile ? '16px' : '8px',
          minWidth: isMobile ? 'auto' : '70px',
          width: isMobile ? '90%' : 'auto',
          background: colors.bgTertiary1,
          padding: isMobile ? '12px 16px' : '16px 12px',
          borderRadius: '16px',
          border: '1px solid #333',
          order: isMobile ? 2 : 1
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onVote(post.id, post.userVote === 1 ? 0 : 1);
            }}
            style={{
              padding: isMobile ? '6px' : '8px',
              borderRadius: '12px',
              border: 'none',
              background: post.userVote === 1 
                ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)' 
                : 'white',
              color: post.userVote === 1 ? 'white' : '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: post.userVote === 1 ? 'scale(1.1)' : 'scale(1)',
              boxShadow: post.userVote === 1 ? '0 4px 12px rgba(16, 185, 129, 0.4)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            <ChevronUp size={isMobile ? 16 : 20} />
          </button>
          
          <span style={{
            fontWeight: 'bold',
            fontSize: isMobile ? '16px' : '20px',
            color: post.userVote === 1 ? '#00d4aa' : 
                   post.userVote === -1 ? '#ef4444' : '#374151',
            transition: 'all 0.3s ease'
          }}>
            {post.votes}
          </span>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onVote(post.id, post.userVote === -1 ? 0 : -1);
            }}
            style={{
              padding: isMobile ? '6px' : '8px',
              borderRadius: '12px',
              border: 'none',
              background: post.userVote === -1 
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
                : 'white',
              color: post.userVote === -1 ? 'white' : '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: post.userVote === -1 ? 'scale(1.1)' : 'scale(1)',
              boxShadow: post.userVote === -1 ? '0 4px 12px rgba(239, 68, 68, 0.4)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            <ChevronDown size={isMobile ? 16 : 20} />
          </button>
        </div>
        
        {/* Content Section */}
        <div style={{ 
          flex: 1, 
          order: isMobile ? 1 : 2,
          width: isMobile ? '100%' : 'auto' // Force full width on mobile
        }}>
          {post.seshRoomInfo ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              padding: isMobile ? '6px 10px' : '8px 12px',
              background: 'linear-gradient(135deg, #3b82f620 0%, #3b82f610 100%)',
              border: '1px solid #3b82f640',
              borderRadius: '12px',
              width: isMobile ? '100%' : 'auto', // Full width on mobile
              boxSizing: 'border-box'
            }}>
              <Users size={isMobile ? 12 : 14} style={{ color: '#3b82f6' }} />
              <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: '500', color: '#3b82f6' }}>
                Sesh Room Thread: {post.seshRoomInfo.name}
              </span>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              padding: isMobile ? '6px 10px' : '8px 12px',
              background: 'linear-gradient(135deg, #10b98120 0%, #10b98110 100%)',
              border: '1px solid #10b98140',
              borderRadius: '12px',
              width: isMobile ? '100%' : 'auto', // Full width on mobile
              boxSizing: 'border-box'
            }}>
              <UserCircle size={isMobile ? 12 : 14} style={{ color: '#10b981' }} />
              <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: '500', color: '#10b981' }}>
                Personal Thread
              </span>
            </div>
          )}

          {/* Author and avatar section - mobile responsive */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: isMobile ? '12px' : '16px', 
            marginBottom: '16px',
            flexDirection: isMobile ? 'column' : 'row',
            width: isMobile ? '100%' : 'auto' // Full width on mobile
          }}>
            {/* Avatar section */}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              alignSelf: isMobile ? 'flex-start' : 'auto',
              gap: isMobile ? '12px' : '0'
            }}>    
              <img
                src={post.avatar}
                alt={post.author}
                style={{
                  width: isMobile ? '40px' : '48px',
                  height: isMobile ? '40px' : '48px',
                  borderRadius: '12px',
                  objectFit: 'cover'
                }}
              />
                      
              {/* Smoker Level Badge */}
              <div style={{
                position: isMobile ? 'static' : 'absolute',
                bottom: isMobile ? 'auto' : '-170%',
                right: isMobile ? 'auto' : 'auto',
                backgroundColor: colors.bgTertiary1,
                borderRadius: '50%',
                width: isMobile ? '40px' : '48px',
                height: isMobile ? '40px' : '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                border: '1px solid #00d4aa'
              }}>
                <img 
                  src={getSmokerEmoji(post.originalData.author_smoke_level)}
                  alt={`${post.originalData.author_smoke_level} level`}
                  style={{
                    width: isMobile ? '36px' : '44px',
                    height: isMobile ? '36px' : '44px',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            </div>

            {/* Thread content */}
            <div style={{ 
              flex: 1, 
              width: isMobile ? '100%' : 'auto',
              minWidth: 0 // Prevent overflow
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                gap: '12px', 
                marginBottom: '8px',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center',
                width: '100%'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '600',
                  color: '#e5e5e5',
                  lineHeight: '1.3',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  {post.title}
                </h3>
                {post.trending && (
                  <div style={{
                    padding: '4px 8px',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    alignSelf: isMobile ? 'flex-start' : 'auto'
                  }}>
                    <Flame size={12} />
                    Trending
                  </div>
                )}
              </div>

              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                marginBottom: '12px',
                flexWrap: 'wrap',
                width: '100%'
              }}>
                <span 
                  className="clickable-username"
                  style={{ 
                    fontSize: isMobile ? '13px' : '14px', 
                    color: '#888',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUsernameClick(post.author_user_id);
                  }}
                >
                  by {post.author}
                </span>
                <span style={{ fontSize: isMobile ? '13px' : '14px', color: '#666' }}>•</span>
                <span style={{ fontSize: isMobile ? '13px' : '14px', color: '#888' }}>
                  {post.createdAt}
                </span>
              </div>

              {post.content && (
                <p style={{
                  margin: '0 0 16px 0',
                  fontSize: isMobile ? '14px' : '15px',
                  color: colors.textPrimary1,
                  lineHeight: '1.5',
                  width: '100%'
                }}>
                  {post.content}
                </p> 
              )}
              
              {/* Poll Component for thread polls */}
              {post.pollData && (
                <div style={{ 
                  margin: '12px 0 16px 0',
                  width: '100%'
                }}>
                  <PollComponent
                    options={post.pollData.pollOptions}
                    totalVotes={post.pollData.totalPollVotes}
                    pollEndsIn={post.pollData.pollEndsIn}
                    userVoted={post.pollData.userVoted}
                    onVote={async (optionId) => {
                      console.log('Poll vote:', optionId);
                    }}
                    page = {'threads'}
                  />
                </div>
              )}

              {/* Bottom action bar - mobile responsive */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '12px' : '0',
                alignItems: isMobile ? 'stretch' : 'center',
                width: '100%'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '20px',
                  justifyContent: isMobile ? 'flex-start' : 'flex-start'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#888' }}>
                    <MessageCircle size={16} />
                    <span style={{ fontSize: isMobile ? '13px' : '14px' }}>{post.comments || 0}</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#888' }}>
                    <Eye size={16} />
                    <span style={{ fontSize: isMobile ? '13px' : '14px' }}>{post.views || 0}</span>
                  </div>
                </div>

                {/* Updated Join Discussion Button with Authentication Check */}
                {isAuthenticated ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenThreadDetail(post);
                    }}
                    style={{
                      padding: isMobile ? '8px 12px' : '8px 16px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      alignSelf: isMobile ? 'stretch' : 'auto',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    Join Discussion →
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Show login prompt or redirect to login
                      alert('Please log in to join the discussion');
                      // Or you could redirect: window.location.href = '/login';
                    }}
                    style={{
                      padding: isMobile ? '8px 12px' : '8px 16px',
                      background: 'transparent',
                      color: '#888',
                      border: '2px solid #333',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      alignSelf: isMobile ? 'stretch' : 'auto',
                      width: isMobile ? '100%' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#8b5cf6';
                      e.currentTarget.style.color = '#8b5cf6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#333';
                      e.currentTarget.style.color = '#888';
                    }}
                  >
                    Login to Join →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Sesh Room Card Component with mobile responsiveness
  const SeshRoomCard = () => (
    <div style={{
      background: colors.bgPrimary,
      border: '1px solid #333',
      borderRadius: '16px',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      padding: isMobile ? '16px' : '20px'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = '#00d4aa';
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 8px 16px rgba(16, 185, 129, 0.2)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = '#333';
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '0'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
            <h3 style={{
              margin: 0,
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              color: '#e5e5e5'
            }}>
              {post.originalData.title}
            </h3>
            {!post.originalData.is_public && <Lock size={isMobile ? 12 : 14} style={{ color: '#8b5cf6' }} />}
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px',
            flexWrap: 'wrap'
          }}>
            <img
              src={post.avatar}
              alt={post.author}
              style={{
                width: isMobile ? '16px' : '20px',
                height: isMobile ? '16px' : '20px',
                borderRadius: '50%'
              }}
            />
            <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#888' }}>
              {post.author}
            </span>
            <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#888' }}>•</span>
            <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#888' }}>
              {post.createdAt}
            </span>
          </div>
        </div>
        
        <div style={{
          padding: '4px 8px',
          background: post.isLive ? '#00d4aa' : '#666',
          borderRadius: '8px',
          fontSize: isMobile ? '9px' : '10px',
          fontWeight: '600',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          alignSelf: isMobile ? 'flex-start' : 'auto'
        }}>
          <div style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: 'white',
            animation: post.isLive ? 'pulse 2s infinite' : 'none'
          }} />
          {post.isLive ? 'LIVE' : 'OFFLINE'}
        </div>
      </div>

      {/* Room Info */}
      {post.content && (
        <p style={{
          margin: '0 0 12px 0',
          fontSize: isMobile ? '13px' : '14px',
          color: '#bbb',
          lineHeight: '1.4'
        }}>
          {post.content}
        </p>
      )}

      <div style={{
        padding: isMobile ? '10px' : '12px',
        background: colors.bgCard,
        borderRadius: '8px',
        marginBottom: '12px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: post.strain ? '8px' : '0',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '4px' : '0'
        }}>
          <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#888' }}>Category:</span>
          <span style={{
            fontSize: isMobile ? '11px' : '12px',
            fontWeight: '600',
            color: '#00d4aa'
          }}>
            {post.category}
          </span>
        </div>
        {post.strain && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '4px' : '0'
          }}>
            <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#888' }}>Strain:</span>
            <span style={{
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: '600',
              color: '#10b981'
            }}>
              {post.strain}
            </span>
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '0'
      }}>
        <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: isMobile ? '11px' : '12px',
            color: '#888'
          }}>
            <Users size={isMobile ? 10 : 12} style={{ color: '#00d4aa' }} />
            {post.participantCount || 0}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: isMobile ? '11px' : '12px',
            color: '#888'
          }}>
            <Eye size={isMobile ? 10 : 12} style={{ color: '#8b5cf6' }} />
            {post.views || 0}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: isMobile ? '11px' : '12px',
            color: '#888'
          }}>
            <MessageCircle size={isMobile ? 10 : 12} style={{ color: '#f59e0b' }} />
            {post.comments || 0}
          </div>
        </div>
      </div>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '4px', 
          marginBottom: '16px',
          justifyContent: isMobile ? 'center' : 'flex-start'
        }}>
          {post.tags.slice(0, isMobile ? 3 : 4).map((tag, i) => (
            <span key={i} style={{
              padding: '2px 6px',
              background: '#333',
              borderRadius: '4px',
              fontSize: isMobile ? '9px' : '10px',
              color: colors.textSecondary1
            }}>
              #{tag}
            </span>
          ))}
          {post.tags.length > (isMobile ? 3 : 4) && (
            <span style={{
              padding: '2px 6px',
              background: '#333',
              borderRadius: '4px',
              fontSize: isMobile ? '9px' : '10px',
              color: '#888'
            }}>
              +{post.tags.length - (isMobile ? 3 : 4)} more
            </span>
          )}
        </div>
      )}

      {/* Join Button */}
      <button 
        onClick={(e) => {
          e.stopPropagation();
          // Handle join room
        }}
        disabled={!post.isLive}
        style={{
          width: '100%',
          padding: isMobile ? '10px' : '12px',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          border: 'none',
          cursor: post.isLive ? 'pointer' : 'not-allowed',
          backgroundColor: post.isLive ? '#00d4aa' : colors.border,
          color: post.isLive ? 'white' : '#666',
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: '600',
          transition: 'all 0.3s ease'
        }}
      >
        {!post.isLive ? (
          <>
            <Video size={isMobile ? 14 : 16} />
            Room Offline
          </>
        ) : (
          <>
            <Video size={isMobile ? 14 : 16} />
            Join Sesh
          </>
        )}
      </button>
    </div>
  );

  // Research Card Component with mobile responsiveness
  const ResearchCard = ({ post, votedArticles, savedArticles, handleVote, handleBookmark, getVoteScore, getSmokerEmoji, onOpenArticle }) => {
    const getCategoryTheme = (category) => {
      const themes = {
        medical: { 
          icon: <Microscope size={isMobile ? 28 : 32} />, 
          gradient: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          bgPattern: '#1e40af15'
        },
        growing: { 
          icon: <Leaf size={isMobile ? 28 : 32} />, 
          gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          bgPattern: '#05966915'
        },
        strains: { 
          icon: <Beaker size={isMobile ? 28 : 32} />, 
          gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          bgPattern: '#7c3aed15'
        },
        general: { 
          icon: <BookOpen size={isMobile ? 28 : 32} />, 
          gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          bgPattern: '#d9770615'
        }
      };
      return themes[category] || themes.general;
    };

    const generateCategoryPattern = (category) => {
      const patterns = {
        medical: (
          <svg width="100%" height="100%" style={{ position: 'absolute', opacity: 0.1 }}>
            <defs>
              <pattern id={`medical-pattern-${category}`} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="2" fill="white" />
                <circle cx="0" cy="0" r="1" fill="white" />
                <circle cx="40" cy="40" r="1" fill="white" />
                <circle cx="0" cy="40" r="1" fill="white" />
                <circle cx="40" cy="0" r="1" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#medical-pattern-${category})`} />
          </svg>
        ),
        growing: (
          <svg width="100%" height="100%" style={{ position: 'absolute', opacity: 0.1 }}>
            <defs>
              <pattern id={`growing-pattern-${category}`} x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
                <path d="M15,5 Q20,10 15,15 Q10,10 15,5" fill="white" />
                <path d="M5,20 Q10,25 15,20" fill="white" />
                <path d="M20,25 Q25,20 30,25" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#growing-pattern-${category})`} />
          </svg>
        ),
        strains: (
          <svg width="100%" height="100%" style={{ position: 'absolute', opacity: 0.1 }}>
            <defs>
              <pattern id={`strains-pattern-${category}`} x="0" y="0" width="25" height="25" patternUnits="userSpaceOnUse">
                <polygon points="12.5,2 20,10 12.5,18 5,10" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#strains-pattern-${category})`} />
          </svg>
        ),
        general: (
          <svg width="100%" height="100%" style={{ position: 'absolute', opacity: 0.1 }}>
            <defs>
              <pattern id={`general-pattern-${category}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <rect x="8" y="8" width="4" height="4" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#general-pattern-${category})`} />
          </svg>
        )
      };
      return patterns[category] || patterns.general;
    };

    const category = post?.category || 'general';
    const theme = getCategoryTheme(category);
    
    return (
      <div style={{
        background: colors.bgPrimary,
        borderRadius: '20px',
        overflow: 'hidden',
        border: '1px solid #333',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#00d4aa';
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 212, 170, 0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#333';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
      onClick={() => onOpenArticle && onOpenArticle(post)}
      >
        {/* Thumbnail with category-specific theme */}
        <div style={{
          height: isMobile ? '150px' : '180px',
          position: 'relative',
          background: theme.gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          {/* Background pattern */}
          {generateCategoryPattern(category)}
          
          {/* Main content */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: isMobile ? '8px' : '12px',
            zIndex: 1,
            textAlign: 'center'
          }}>
            <div style={{
              width: isMobile ? '48px' : '64px',
              height: isMobile ? '48px' : '64px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}>
              {theme.icon}
            </div>
            
            <div style={{
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.9)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              background: 'rgba(0, 0, 0, 0.3)',
              padding: isMobile ? '4px 8px' : '6px 12px',
              borderRadius: '8px',
              backdropFilter: 'blur(5px)'
            }}>
              {category.charAt(0).toUpperCase() + category.slice(1)} Research
            </div>
            
            <div style={{
              fontSize: isMobile ? '10px' : '11px',
              color: 'rgba(255, 255, 255, 0.7)',
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '4px 8px',
              borderRadius: '6px',
              textTransform: 'capitalize'
            }}>
              Research Paper
            </div>
          </div>
          
          {/* Badges - mobile responsive */}
          <div style={{
            position: 'absolute',
            top: isMobile ? '8px' : '12px',
            left: isMobile ? '8px' : '12px',
            display: 'flex',
            gap: isMobile ? '4px' : '8px',
            flexWrap: 'wrap'
          }}>
            <span style={{
              padding: isMobile ? '3px 6px' : '4px 8px',
              background: '#3b82f6',
              color: 'white',
              borderRadius: '8px',
              fontSize: isMobile ? '9px' : '11px',
              fontWeight: '600'
            }}>
              Research
            </span>
            
            {post?.is_peer_reviewed && (
              <span style={{
                padding: isMobile ? '3px 6px' : '4px 8px',
                background: '#fbbf24',
                color: 'white',
                borderRadius: '8px',
                fontSize: isMobile ? '9px' : '11px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Award size={isMobile ? 8 : 10} />
                {isMobile ? 'Peer' : 'Peer Reviewed'}
              </span>
            )}
          </div>
          
          <div style={{
            position: 'absolute',
            top: isMobile ? '8px' : '12px',
            right: isMobile ? '8px' : '12px',
            display: 'flex',
            gap: isMobile ? '4px' : '8px',
            flexWrap: 'wrap'
          }}>
            <span style={{
              padding: isMobile ? '3px 6px' : '4px 8px',
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              borderRadius: '8px',
              fontSize: isMobile ? '9px' : '11px',
              fontWeight: '600'
            }}>
              {post?.difficulty || 'Beginner'}
            </span>
          </div>
          
          {/* Engagement score overlay */}
          <div style={{
            position: 'absolute',
            bottom: isMobile ? '8px' : '12px',
            right: isMobile ? '8px' : '12px',
            padding: isMobile ? '4px 8px' : '6px 10px',
            background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
            borderRadius: '12px',
            color: 'white',
            fontSize: isMobile ? '10px' : '12px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Zap size={isMobile ? 10 : 12} />
            {post?.engagement_score || 85}%
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: isMobile ? '16px' : '20px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px',
            flexWrap: 'wrap'
          }}>
            <span style={{
              padding: '2px 8px',
              background: theme.bgPattern,
              color: category === 'medical' ? '#3b82f6' :
                     category === 'growing' ? '#10b981' :
                     category === 'strains' ? '#8b5cf6' : '#f59e0b',
              borderRadius: '8px',
              fontSize: isMobile ? '10px' : '11px',
              fontWeight: '600'
            }}>
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </span>
            
            <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#666' }}>•</span>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Clock size={isMobile ? 10 : 12} style={{ color: '#888' }} />
              <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#888' }}>
                {post?.read_time || 5} min read
              </span>
            </div>
          </div>
          
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: isMobile ? '16px' : '18px',
            fontWeight: '600',
            color: '#e5e5e5',
            lineHeight: '1.3'
          }}>
            {post?.title?.replace('📚 ', '') || 'Untitled Research'}
          </h3>
          
          <p style={{
            margin: '0 0 16px 0',
            fontSize: isMobile ? '13px' : '14px',
            color: '#bbb',
            lineHeight: '1.5',
            display: '-webkit-box',
            WebkitLineClamp: isMobile ? 2 : 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {post?.content || post?.excerpt || post?.description}
          </p>
        
          {/* Stats section - mobile responsive */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '12px' : '16px',
            marginBottom: '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px', 
              color: '#888',
              transition: 'color 0.3s ease' 
            }}>
              <Eye size={isMobile ? 12 : 14} />
              <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: '500' }}>
                {(post?.views || post?.view_count || 0).toLocaleString()}
              </span>
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px', 
              color: '#888',
              transition: 'color 0.3s ease' 
            }}>
              <MessageCircle size={isMobile ? 12 : 14} />
              <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: '500' }}>
                {post?.comments || post?.discussion_count || 0}
              </span>
            </div>
          </div>

          {/* LAYOUT: Mobile = stacked, Desktop = side by side */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: isMobile ? '12px' : '20px',
            marginBottom: '16px',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            
            {/* Voting section - mobile responsive */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'row' : 'column',
              alignItems: 'center',
              justifyContent: isMobile ? 'center' : 'flex-start',
              gap: isMobile ? '16px' : '8px',
              minWidth: isMobile ? 'auto' : '70px',
              width: isMobile ? '100%' : 'auto',
              background: colors.bgTertiary1,
              padding: isMobile ? '12px 16px' : '16px 12px',
              borderRadius: '16px',
              border: '1px solid #333'
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleVote(post.id, votedArticles.get(post.id) === 'up' ? null : 'up');
                }}
                style={{
                  padding: isMobile ? '6px' : '8px',
                  borderRadius: '12px',
                  border: 'none',
                  background: votedArticles.get(post.id) === 'up'
                    ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                    : 'white',
                  color: votedArticles.get(post.id) === 'up' ? 'white' : '#6b7280',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: votedArticles.get(post.id) === 'up' ? 'scale(1.1)' : 'scale(1)',
                  boxShadow: votedArticles.get(post.id) === 'up' 
                    ? '0 4px 12px rgba(16, 185, 129, 0.4)' 
                    : '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
              >
                <ChevronUp size={isMobile ? 16 : 20} />
              </button>
              
              <span style={{
                fontWeight: 'bold',
                fontSize: isMobile ? '16px' : '20px',
                color: votedArticles.get(post.id) === 'up' ? '#00d4aa' : 
                      votedArticles.get(post.id) === 'down' ? '#ef4444' : '#374151',
                transition: 'all 0.3s ease'
              }}>
                {getVoteScore(post)}
              </span>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleVote(post.id, votedArticles.get(post.id) === 'down' ? null : 'down');
                }}
                style={{
                  padding: isMobile ? '6px' : '8px',
                  borderRadius: '12px',
                  border: 'none',
                  background: votedArticles.get(post.id) === 'down'
                    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                    : 'white',
                  color: votedArticles.get(post.id) === 'down' ? 'white' : '#6b7280',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: votedArticles.get(post.id) === 'down' ? 'scale(1.1)' : 'scale(1)',
                  boxShadow: votedArticles.get(post.id) === 'down' 
                    ? '0 4px 12px rgba(239, 68, 68, 0.4)' 
                    : '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
              >
                <ChevronDown size={isMobile ? 16 : 20} />
              </button>
            </div>

            {/* Author and submitter section - mobile responsive */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              {/* Author section */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: isMobile ? '10px' : '12px',
                background: colors.bgCard,
                borderRadius: '12px',
                border: '1px solid #333'
              }}>
                <div style={{
                  width: isMobile ? '28px' : '32px',
                  height: isMobile ? '28px' : '32px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: '600'
                }}>
                  {(post?.author || 'Unknown').charAt(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#e5e5e5', fontWeight: '500' }}>
                    {post?.author || 'Unknown Author'}
                  </div>
                  <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#888' }}>
                    {post?.institution || 'Research Institution'}
                  </div>
                </div>
                <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#888' }}>
                  {post?.publishedDate || post?.published_date || post?.createdAt || post?.created_at}
                </div>
              </div>

              {/* Submitter section */}
              {post?.originalData?.submitter && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 0 0 0',
                  borderTop: '1px solid #333',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '12px' : '0',
                  paddingLeft: !isMobile ? '60px' : '0' // ADD THIS LINE HERE
                }}>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: isMobile ? '11px' : '12px',
                    color: '#888',
                    width: isMobile ? '100%' : 'auto' // Full width on mobile
                  }}>
                    <div style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? '8px' : '0' // ADD THIS BACK - crucial for mobile spacing
                    }}>    
                      <img
                        src={
                          post?.originalData?.submitter?.profile_image_url ||
                          (post?.originalData?.submitter?.smoker_level === 'stoner' ? `/peng1.png` : 
                          post?.originalData?.submitter?.smoker_level === 'beginner' ? `/chick1.png` : 
                          post?.originalData?.submitter?.smoker_level === 'connoisseur' ? `/dog.png` : 
                            `/pengu3.png`)
                        }
                        alt={post?.originalData?.submitter?.username}
                        style={{
                          width: isMobile ? '32px' : '40px',
                          height: isMobile ? '32px' : '40px',
                          borderRadius: '12px',
                          objectFit: 'cover'
                        }}
                      />
                              
                      {/* Smoker Level Badge - Fixed positioning */}
                      <div style={{
                        position: isMobile ? 'static' : 'absolute', // Static on mobile, absolute on desktop
                        bottom: isMobile ? 'auto' : '0%',
                        right: isMobile ? 'auto' : '150%',
                        backgroundColor: colors.bgTertiary1,
                        borderRadius: '50%',
                        width: isMobile ? '32px' : '40px',
                        height: isMobile ? '32px' : '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        border: '1px solid #00d4aa'
                      }}>
                        <img 
                          src={getSmokerEmoji(post?.originalData?.submitter?.smoker_level)}
                          alt={`${post?.originalData?.submitter?.smoker_level} level`}
                          style={{
                            width: isMobile ? '28px' : '36px',
                            height: isMobile ? '28px' : '36px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                    </div>

                    {/* Submitter details */}
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '4px',
                        flexWrap: 'wrap'
                      }}>
                        <span 
                          className="clickable-username"
                          style={{
                            fontSize: isMobile ? '12px' : '13px',
                            fontWeight: '600',
                            color: '#e5e5e5',
                            cursor: 'pointer',
                            position: 'relative'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUsernameClick(post?.originalData?.submitter?.id);
                          }}
                        >
                          Submitted by {post?.originalData?.submitter?.handle || post?.originalData?.submitter?.username}
                        </span>
                        <div style={{
                          padding: '2px 6px',
                          background: '#00d4aa20',
                          color: '#00d4aa',
                          borderRadius: '6px',
                          fontSize: isMobile ? '9px' : '10px',
                          fontWeight: '600',
                          textTransform: 'capitalize'
                        }}>
                          {post?.originalData?.submitter?.smoker_level}
                        </div>
                      </div>
                      
                      <div style={{
                        fontSize: isMobile ? '11px' : '12px',
                        color: '#888'
                      }}>
                        {post?.originalData?.submitted_at} • Member since {post?.originalData?.submitter?.created_at}
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px',
                    alignSelf: isMobile ? 'stretch' : 'auto',
                    justifyContent: isMobile ? 'center' : 'flex-end'
                  }}>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBookmark(post.id);
                      }}
                      style={{
                        padding: isMobile ? '6px' : '8px',
                        background: savedArticles.has(post.id) 
                          ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' 
                          : 'transparent',
                        border: '1px solid',
                        borderColor: savedArticles.has(post.id) ? '#fbbf24' : '#333',
                        borderRadius: '8px',
                        color: savedArticles.has(post.id) ? 'white' : '#888',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Bookmark size={isMobile ? 10 : 12} />
                    </button>
                    
                    <button style={{
                      padding: isMobile ? '6px' : '8px',
                      background: 'transparent',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: '#888',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}>
                      <Share2 size={isMobile ? 10 : 12} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Call to action */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onOpenArticle(post);
            }}
            style={{
              width: '100%',
              padding: isMobile ? '10px' : '12px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: '600',
              marginTop: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
          >
            Read Full Study
            <ArrowRight size={isMobile ? 12 : 14} />
          </button>
        </div>
      </div>
    );
  };

  // Render appropriate card based on post type
  switch (post.type) {
    case 'thread':
      return <ThreadCard />;
    case 'sesh_room':
      return <SeshRoomCard />;
    case 'research':
      return <ResearchCard 
        post={post}
        votedArticles={new Map()} 
        savedArticles={new Set()} 
        handleVote={() => {}} 
        handleBookmark={() => {}} 
        getVoteScore={() => post.votes || 0} 
        getSmokerEmoji={getSmokerEmoji} 
        onOpenArticle={onOpenArticle}
      />;
    default:
      return <ThreadCard />;
  }
};

export function CommunityPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProfileView, setShowProfileView] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showArticleDetail, setShowArticleDetail] = useState(false);
  const [tags, setTags] = useState([]);
  const [achievementsLoading, setAchievementsLoading] = useState(true);
  const [sortBy, setSortBy] = useState('new');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [profileData, setProfileData] = useState({ achievements: [] });
  const [notifications, setNotifications] = useState(3);
  const [userStats, setUserStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all');

  const [showThreadDetail, setShowThreadDetail] = useState(false);
  const [selectedThreadForDetail, setSelectedThreadForDetail] = useState(null);

  const { colors } = useTheme();

  // Add mobile state management
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const { user, isAuthenticated } = useUser();

  // Add resize listener
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getCurrentUserId = () => {
    return user?.id || null; 
  };

  const handleOpenArticle = async (article) => {
    // Extract the original ID for research articles
    let articleToOpen = article;
    if (article.type === 'research' && article.id.startsWith('research_')) {
      articleToOpen = {
        ...article,
        id: article.originalData.id // Use the original numeric ID
      };
    }
    
    setSelectedArticle(articleToOpen);
    setShowArticleDetail(true);
    
    try {
      // Use the original ID for API calls
      const originalId = article.type === 'research' && article.id.startsWith('research_') 
        ? article.originalData.id 
        : article.id;
        
      const viewData = await researchAPI.trackArticleView(originalId);
      if (viewData && viewData.success) {
        setPosts(prevPosts => 
          prevPosts.map(post => 
            post.id === article.id 
              ? { 
                  ...post, 
                  views: viewData.view_count, 
                  originalData: { 
                    ...post.originalData, 
                    view_count: viewData.view_count 
                  } 
                }
              : post
          )
        );
      }
    } catch (error) {
      console.warn('Failed to track article view:', error);
    }
  };

  const convertToThreadFormat = (communityPost) => {
    if (communityPost.type !== 'thread') return null;
    
    return {
      id: communityPost.originalData.id,
      title: communityPost.title,
      description: communityPost.content,
      author: communityPost.author,
      author_handle: communityPost.author,
      author_user_id: communityPost.author_user_id,
      avatar: communityPost.avatar,
      user_avatar: communityPost.avatar,
      created_at: communityPost.createdAt,
      likes: communityPost.votes,
      user_vote: communityPost.userVote,
      messages: communityPost.comments,
      views: communityPost.views,
      trending: communityPost.trending,
      author_smoke_level: communityPost.originalData.author_smoke_level,
      sesh_room_info: communityPost.seshRoomInfo,
      type: communityPost.pollData ? 'poll' : 'thread',
      pollOptions: communityPost.pollData?.pollOptions,
      totalPollVotes: communityPost.pollData?.totalPollVotes,
      pollEndsIn: communityPost.pollData?.pollEndsIn,
      poll: communityPost.pollData ? { has_voted: communityPost.pollData.userVoted } : null
    };
  };

  const handleOpenThreadDetail = (post) => {
    console.log('OPEN THREAD')
    if (post.type === 'thread') {
      console.log('OPEN THREAD2')
      const threadData = convertToThreadFormat(post);
      setSelectedThreadForDetail(threadData);
      setShowThreadDetail(true);
    }
  };

  // Fetch user stats
  const fetchUserStats = async () => {
    try {
      setStatsLoading(true);
      const response = await fetch(`${API_URL}/api/user/stats`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (result.success) {
        setUserStats(result.data);
      } else {
        console.error('Failed to fetch user stats:', result.error);
        setUserStats({
          level: 1,
          xp: 0,
          nextLevelXp: 100,
          get_next_level_threshold: 100,
          streak: 0,
          badges: [],
          reputation: 0,
          posts: 0,
          comments: 0,
          upvotes: 0,
          threads: 0,
          reviews: 0,
          helpful_answers: 0,
          sesh_rooms_joined: 0
        });
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      setUserStats({
        level: 1,
        xp: 0,
        nextLevelXp: 100,
        get_next_level_threshold: 100,
        streak: 0,
        badges: [],
        reputation: 0,
        posts: 0,
        comments: 0,
        upvotes: 0,
        threads: 0,
        reviews: 0,
        helpful_answers: 0,
        sesh_rooms_joined: 0
      });
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchAchievements = async () => {
    try {
      setAchievementsLoading(true);
      const response = await fetch(`${API_URL}/api/achievements`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (result.success) {
        setProfileData(result.data);
      } else {
        console.error('Failed to fetch achievements:', result.error);
        setProfileData({ achievements: [] });
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
      setProfileData({ achievements: [] });
    } finally {
      setAchievementsLoading(false);
    }
  };

  // Fetch threads activity
  const fetchThreadsActivity = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/get-threads`, {
        params: {
          page: 1,
          limit: 10,
          sortBy: 'new',
          user_id: user?.id,
        },
        withCredentials: true
      });
      return response.data.threads || [];
    } catch (error) {
      console.error('Failed to fetch threads:', error);
      return [];
    }
  };

  // Fetch sesh rooms activity
  const fetchSeshActivity = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rooms`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const rooms = data.rooms || [];
        const publicRooms = rooms.filter(room => room.type !== 'private');
        return publicRooms;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch sesh rooms:', error);
      return [];
    }
  };

  // Fetch research activity
  const fetchResearchActivity = async () => {
    try {
      const response = await fetch(`${API_URL}/articles?page=1&per_page=20&category=all&search=&sort_by=hot&tag=`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        return data.articles || [];
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch research articles:', error);
      return [];
    }
  };

  // Main function to fetch and combine all activity
  const fetchCommunityActivity = async () => {
    try {
      setLoading(true);
      setError(null);

      const [threadsData, seshData, researchData] = await Promise.all([
        fetchThreadsActivity(),
        fetchSeshActivity(),
        fetchResearchActivity()
      ]);

      const allPosts = [
        ...threadsData.map(item => normalizeContentItem(item, 'thread')),
        ...seshData.map(item => normalizeContentItem(item, 'sesh_activity')),
        ...researchData.map(item => normalizeContentItem(item, 'research'))
      ];

      const sortedPosts = allPosts.sort((a, b) => {
        const dateDiff = b.sortDate - a.sortDate;
        if (Math.abs(dateDiff) < 60000) {
          const aScore = a.votes + a.comments * 2 + (a.trending ? 10 : 0) + (a.hot ? 15 : 0);
          const bScore = b.votes + b.comments * 2 + (b.trending ? 10 : 0) + (b.hot ? 15 : 0);
          return bScore - aScore;
        }
        return dateDiff;
      });

      setPosts(sortedPosts);
    } catch (error) {
      console.error('Failed to fetch community activity:', error);
      setError('Failed to load community activity');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(`${API_URL}/api/research/tags`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (result.success) {
        setTags(result.data.tags);
      } else {
        console.error('Failed to fetch tags:', result.error);
        setTags([]);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    }
  };

  // Load data on component mount
  useEffect(() => {
    if (user?.id) {
      fetchUserStats();
      fetchAchievements();
    }
    fetchCommunityActivity();
    fetchTags();
  }, [user]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .clickable-username {
        position: relative;
        cursor: pointer;
        transition: color 0.3s ease;
      }
      
      .clickable-username::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 0;
        height: 1px;
        background: linear-gradient(135deg, #00d4aa 0%, #10b981 100%);
        transition: width 0.3s ease;
      }
      
      .clickable-username:hover {
        color: #00d4aa !important;
      }
      
      .clickable-username:hover::after {
        width: 100%;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (style.parentNode) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Handle voting
  const handleVote = async (postId, direction) => {
    const [type, originalId] = postId.split('_');
    
    try {
      if (type === 'thread') {
        await axios.post(`${API_URL}/api/thread/vote`, {
          thread_id: originalId,
          user_id: user?.id,
          value: direction
        }, {
          withCredentials: true
        });
      }
      
      setPosts(posts.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              userVote: post.userVote === direction ? 0 : direction,
              votes: post.votes + (post.userVote === direction ? -direction : direction - (post.userVote || 0))
            }
          : post
      ));
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const handleBookmark = (postId) => {
    console.log('Bookmarked post:', postId);
  };

  const handleShare = (post) => {
    console.log('Sharing post:', post.title);
  };

  const handleReward = (postId, emoji) => {
    console.log('Rewarding post with:', emoji);
  };

  // Filter and sort posts
  const filteredPosts = posts
    .filter(post => {
      const matchesSearch = !searchTerm || 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (post.content && post.content.toLowerCase().includes(searchTerm.toLowerCase())) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesFilter = selectedFilter === 'all' || 
        (selectedFilter === 'threads' && post.type === 'thread') ||
        (selectedFilter === 'sesh' && post.type === 'sesh_room') ||
        (selectedFilter === 'research' && post.type === 'research') ||
        (selectedFilter === 'live' && post.isLive);
      
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'hot': 
          return (b.votes + b.comments * 2 + (b.trending ? 10 : 0) + (b.hot ? 15 : 0) + b.engagement) - 
                 (a.votes + a.comments * 2 + (a.trending ? 10 : 0) + (a.hot ? 15 : 0) + a.engagement);
        case 'new': 
          const aDate = new Date(a.originalData.created_at || a.originalData.published_date || a.originalData.publishedDate);
          const bDate = new Date(b.originalData.created_at || b.originalData.published_date || b.originalData.publishedDate);
          return bDate - aDate;
        case 'top': 
          return b.votes - a.votes;
        case 'trending':
          return (b.trending ? 1 : 0) - (a.trending ? 1 : 0);
        default: 
          return 0;
      }
    });

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: '48px',
            marginBottom: '16px',
            animation: 'spin 1s linear infinite'
          }}>🌿</div>
          <p style={{ color: '#888' }}>Loading community activity...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <p style={{ color: '#ef4444' }}>{error}</p>
          <button
            onClick={fetchCommunityActivity}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              marginTop: '16px'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0a0a',
      color: 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .slide-in {
          animation: slideIn 0.5s ease-out;
        }
      `}</style>

      <div style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: isMobile ? '16px' : '24px' 
      }}>
        {/* Enhanced Header with mobile responsiveness */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: isMobile ? '24px' : '32px',
          background: colors.comeCard,
          padding: isMobile ? '16px 20px' : '24px 32px',
          borderRadius: '20px',
          border: '1px solid #333',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '16px' : '0'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            <div style={{
              width: isMobile ? '50px' : '60px',
              height: isMobile ? '50px' : '60px',
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)'
            }}>
              <span style={{ fontSize: isMobile ? '28px' : '32px', color: 'white' }}>🌿</span>
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: isMobile ? '24px' : '32px',
                fontWeight: '800',
                background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: '1.2'
              }}>
                Community Feed
              </h1>
              <p style={{
                margin: 0,
                color: '#888',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '500'
              }}>
                Latest from threads, sesh rooms, and research 📡
              </p>
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '8px' : '16px',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'center' : 'flex-end'
          }}>
            <button
              onClick={() => fetchCommunityActivity()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '8px' : '12px',
                padding: isMobile ? '12px 16px' : '16px 24px',
                background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                flex: isMobile ? 1 : 'auto'
              }}
            >
              <Sparkles size={isMobile ? 16 : 20} />
              {isMobile ? 'Refresh' : 'Refresh Feed'}
            </button>
          </div>
        </div>

        {/* Updated grid layout */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 3fr 1fr', 
          gap: isMobile ? '24px' : '32px' 
        }}>
          {/* Left Sidebar - Hidden on mobile */}
          {!isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <UserLevelCard 
                stats={userStats} 
                isAuthenticated={isAuthenticated} 
              />
              <AchievementsCard 
                profileData={profileData} 
                loading={achievementsLoading} 
                isAuthenticated={isAuthenticated} 
              />
            </div>
          )}

          {/* Main Content */}
          <div style={{ order: isMobile ? 1 : 0 }}>
            {/* Enhanced Search and Filters */}
            <div style={{
              background: colors.bgCard,
              borderRadius: '20px',
              padding: isMobile ? '16px' : '24px',
              border: '2px solid #333',
              color: '#e5e5e5',
              marginBottom: '24px',
              width: isMobile ? '90%' : '',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '16px', 
                marginBottom: '16px',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <div style={{ position: 'relative', flex: 1, width: isMobile ? '100%' : 'auto' }}>
                  <Search size={20} style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#888'
                  }} />
                  <input
                    type="text"
                    placeholder="Search community activity... 🔍"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      paddingLeft: '48px',
                      paddingRight: '16px',
                      paddingTop: isMobile ? '12px' : '16px',
                      paddingBottom: isMobile ? '12px' : '16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '16px',
                      fontSize: isMobile ? '14px' : '16px',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#00d4aa';
                      e.currentTarget.style.boxShadow = '0 0 0 4px rgba(16, 185, 129, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  <Filter size={16} style={{ color: '#888' }} />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      outline: 'none',
                      cursor: 'pointer',
                      background: 'white',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    <option value="hot">🔥 Hot</option>
                    <option value="new">⚡ New</option>
                    <option value="top">🏆 Top</option>
                    <option value="trending">📈 Trending</option>
                  </select>
                </div>
              </div>

              {/* Content Type Filters with mobile centering */}
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px',
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}>
                {[
                  { key: 'all', label: 'All Activity', icon: '🌍' },
                  { key: 'threads', label: 'Threads', icon: '💬' },
                  { key: 'sesh', label: 'Sesh Rooms', icon: '🎥' },
                  { key: 'research', label: 'Research', icon: '🔬' },
                  { key: 'live', label: 'Live Now', icon: '🔴' }
                ].map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => setSelectedFilter(filter.key)}
                    style={{
                      padding: isMobile ? '6px 12px' : '8px 16px',
                      background: selectedFilter === filter.key 
                        ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)' 
                        : 'white',
                      color: selectedFilter === filter.key ? 'white' : '#6b7280',
                      border: '1px solid',
                      borderColor: selectedFilter === filter.key ? '#00d4aa' : '#e5e7eb',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedFilter !== filter.key) {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)';
                        e.currentTarget.style.borderColor = '#00d4aa';
                        e.currentTarget.style.color = '#00d4aa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedFilter !== filter.key) {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}
                  >
                    <span>{filter.icon}</span>
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile Stats Cards - Show above content on mobile */}
            {isMobile && (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '16px', 
                marginBottom: '24px' 
              }}>
                <UserLevelCard 
                  stats={userStats} 
                  isAuthenticated={isAuthenticated} 
                />
                <AchievementsCard 
                  profileData={profileData} 
                  loading={achievementsLoading} 
                  isAuthenticated={isAuthenticated} 
                />
                <MobileStatsToggle isMobile={isMobile} colors={colors} />
              </div>
            )}

            {/* Activity Feed with mobile-responsive cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '16px' : '24px' }}>
              {filteredPosts.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: isMobile ? '32px 16px' : '48px 24px',
                  background: colors.bgTertiary1,
                  borderRadius: '20px',
                  border: '1px solid #333'
                }}>
                  <div style={{ fontSize: isMobile ? '40px' : '48px', marginBottom: '16px' }}>🌱</div>
                  <h3 style={{ 
                    margin: '0 0 8px 0', 
                    color: '#e5e5e5', 
                    fontSize: isMobile ? '18px' : '20px', 
                    fontWeight: '600' 
                  }}>
                    {searchTerm || selectedFilter !== 'all' ? 'No activity found' : 'No recent activity'}
                  </h3>
                  <p style={{ 
                    margin: '0 0 24px 0', 
                    color: '#888', 
                    fontSize: isMobile ? '14px' : '16px' 
                  }}>
                    {searchTerm || selectedFilter !== 'all' 
                      ? 'Try adjusting your filters or search terms' 
                      : 'Be the first to create some content!'}
                  </p>
                  {(!searchTerm && selectedFilter === 'all') && (
                    <button
                      onClick={() => window.location.href = '/threads'}
                      style={{
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: '600'
                      }}
                    >
                      Start a Discussion
                    </button>
                  )}
                </div>
              ) : (
                filteredPosts.map((post, index) => (
                  <div key={post.id} className="slide-in" style={{
                    animationDelay: `${index * 0.1}s`,
                    width: isMobile ? '100%' : '',
                    margin: isMobile ? '0' : '0'
                  }}>
                    <CommunityPostCard
                      post={post}
                      onVote={handleVote}
                      onBookmark={handleBookmark}
                      onShare={handleShare}
                      onReward={handleReward}
                      onOpenArticle={handleOpenArticle}
                      onOpenThreadDetail={handleOpenThreadDetail}
                      setShowProfileView={setShowProfileView}
                      setSelectedUserId={setSelectedUserId}
                      getCurrentUserId={getCurrentUserId}
                      isAuthenticated={isAuthenticated}
                      isMobile={isMobile}
                      colors={colors}
                    />
                  </div>
                ))
              )}
            </div>

            {/* Load More Section */}
            {filteredPosts.length > 0 && (
              <div style={{
                marginTop: '32px',
                textAlign: 'center',
                padding: isMobile ? '24px 16px' : '32px',
                background: colors.bgTertiary1,
                borderRadius: '20px',
                border: '1px solid #333'
              }}>
                <div style={{ marginBottom: '16px' }}>
                  <span style={{ fontSize: isMobile ? '28px' : '32px', marginBottom: '8px', display: 'block' }}>🚀</span>
                  <h3 style={{
                    margin: '0 0 8px 0',
                    fontSize: isMobile ? '18px' : '20px',
                    fontWeight: '600',
                    color: '#e5e5e5'
                  }}>
                    You're all caught up!
                  </h3>
                  <p style={{
                    margin: 0,
                    color: '#888',
                    fontSize: '14px'
                  }}>
                    You've seen all the latest activity. Check back soon for more updates!
                  </p>
                </div>
                <button
                  onClick={fetchCommunityActivity}
                  style={{
                    padding: isMobile ? '12px 24px' : '16px 32px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)'
                  }}
                >
                  ✨ Refresh for New Content
                </button>
              </div>
            )}
          </div>

          {/* Right Sidebar - Hidden on mobile */}
          {!isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <CommunityStatsCard />
              <TrendingCard />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showArticleDetail && selectedArticle && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: '#0a0a0a',
            overflow: 'hidden'
          }}
        >
          <div style={{
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}>
            <ResearchArticleDetail 
              handleOpenArticle={handleOpenArticle}
              article={selectedArticle}
              tags={tags}
              onBack={() => {
                setShowArticleDetail(false);
                setSelectedArticle(null);
              }}
            />
          </div>
        </div>
      )}

      {showProfileView && selectedUserId && (
        <PublicProfileView
          userId={selectedUserId}
          currentUserId={getCurrentUserId()}
          onClose={() => {
            setShowProfileView(false);
            setSelectedUserId(null);
          }}
        />
      )}
      {showThreadDetail && selectedThreadForDetail && (
        <ThreadDetailView 
          thread={selectedThreadForDetail}
          onClose={() => {
            setShowThreadDetail(false);
            setSelectedThreadForDetail(null);
          }}
          currentUserId={getCurrentUserId()}
        />
      )}
    </div>
  );
}